import { useState, useRef, useCallback } from 'react';

/**
 * pickRecorderMime — tarayıcı uyumluluğu için MIME seç
 */
function pickRecorderMime({ video = false, audio = true } = {}) {
  const candidates = [];
  if (video) {
    candidates.push(
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    );
  }
  if (audio && !video) {
    candidates.push(
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/mpeg',
    );
  }
  for (const candidate of candidates) {
    try {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(candidate)) {
        return candidate;
      }
    } catch {
      // continue
    }
  }
  return '';
}

function mimeToExt(mimeType) {
  if (!mimeType) return 'webm';
  if (mimeType.startsWith('audio/mp4') || mimeType.startsWith('video/mp4')) return 'mp4';
  if (mimeType.startsWith('audio/mpeg')) return 'mp3';
  return 'webm';
}

/**
 * useMediaRecorder
 *
 * @param {'audio'|'video'} type
 * @returns hook API
 *
 * Usage:
 *   const { isRecording, blobUrl, blob, duration, start, stop, reset, error } = useMediaRecorder('audio');
 */
export function useMediaRecorder(type = 'audio') {
  const [isRecording, setIsRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async ({ onStream } = {}) => {
    setError(null);
    setBlob(null);
    setBlobUrl(null);
    setDuration(0);
    chunksRef.current = [];

    try {
      const constraints = type === 'video'
        ? { audio: true, video: true }
        : { audio: true };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (typeof onStream === 'function') onStream(stream);

      const mimeType = pickRecorderMime({ video: type === 'video', audio: true });
      let recorder;
      if (mimeType) {
        try {
          recorder = new MediaRecorder(stream, { mimeType });
        } catch {
          recorder = new MediaRecorder(stream);
        }
      } else {
        recorder = new MediaRecorder(stream);
      }

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const finalMime = recorder.mimeType || (type === 'video' ? 'video/webm' : 'audio/webm');
        const recorded = new Blob(chunksRef.current, { type: finalMime });
        setBlob(recorded);
        const url = URL.createObjectURL(recorded);
        setBlobUrl(url);
        stopTracks();
      };

      recorder.start(250);
      setIsRecording(true);

      // Timer
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
        setError('permission_denied');
      } else {
        setError(err.message);
      }
    }
  }, [type, stopTracks]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const reset = useCallback(() => {
    stop();
    stopTracks();
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null);
    setBlobUrl(null);
    setDuration(0);
    setError(null);
    chunksRef.current = [];
  }, [stop, stopTracks, blobUrl]);

  return {
    isRecording,
    blob,
    blobUrl,
    duration,
    error,
    permissionDenied,
    start,
    stop,
    reset,
    mimeToExt,
    streamRef,
  };
}
